import { useEffect } from "react";

import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import Cookies from "js-cookie";

import {
  Button,
  IconButton,
  useBreakpointValue,
  FiEdit
} from "@/storybook-components";

import useAuthGate from "@/hooks/useAuthGate";
import useUser from "@/hooks/useUser";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import useUnverifiedModal from "@/hooks/useUnverfiedModal";
import {
  setTriggerPoint,
  setModalStatus
} from "@/redux/features/unverified-modal/slice";
import { setTriggerPointCookie } from "@/helpers/set-trigger-point-cookie";
import { getToolLocaleKeyFromLayout } from "@/helpers/get-tool-locale-key-from-layout";
import { toSentenceCase } from "@/helpers/to-sentence-case";

const ActionButton = ({
  projectVisibility,
  canContribute,
  isVotingAllowed,
  allowUnverifiedParticipation,
  onOpenHandler
}): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { isUnverfiedUserLoggedIn, isRegisteredUserLoggedIn } = useUser();
  const {
    isGuestUser,
    isGuestParticipationEnabled,
    openGuestLoginModal,
    requireProfileCompletion
  } = useAuthGate();
  const { ideas, brainstormer } = useAppSelector(
    (state) => state?.brainstormers
  );

  const { layout, toolConfigurationAttributes } = brainstormer;
  const contributionName =
    toolConfigurationAttributes?.preferences?.contributionName || "";

  const { triggerPoint } = useAppSelector((state) => state.unverifiedModal);
  const cookieValues = Cookies.get("trigger_point");
  let cookieTriggerPoint;
  if (cookieValues) {
    cookieTriggerPoint = JSON.parse(cookieValues);
  }

  const { action } = router.query;
  const isIdeaBoardEmpty = ideas.length === 0;
  const isMobileScreen = useBreakpointValue({
    base: true,
    xl: false
  });

  const isButtonDisabled =
    ((projectVisibility === "Panelled" || projectVisibility === "Protected") &&
      !canContribute) ||
    isVotingAllowed;

  const guestParticipationAllowed =
    isGuestParticipationEnabled && allowUnverifiedParticipation;

  const triggerPayloadExtras = () => ({
    projectId:
      typeof router.query?.projectId === "string"
        ? router.query.projectId
        : undefined,
    toolId:
      typeof router.query?.brainstormerId === "string"
        ? router.query.brainstormerId
        : undefined
  });

  useUnverifiedModal(allowUnverifiedParticipation, (isProceedAllowed) => {
    if (isProceedAllowed) {
      dispatch(
        setTriggerPoint({ ...triggerPoint, isTriggeredPointActive: false })
      );
      if (
        triggerPoint.triggeredSource === "new_idea" ||
        cookieTriggerPoint?.triggeredSource === "new_idea"
      ) {
        onOpenHandler();
        addQueryParam();
      }
      Cookies.remove("trigger_point");
    }
  });
  const handleClick = () => {
    if (isButtonDisabled) return;

    if (isGuestUser) {
      if (!allowUnverifiedParticipation) {
        if (!isGuestParticipationEnabled) {
          dispatch(setModalStatus("create"));
          const activeNewIdeaTrigger = {
            triggeredSource: "new_idea",
            isTriggeredPointActive: true,
            ...triggerPayloadExtras()
          };
          setTriggerPointCookie(activeNewIdeaTrigger);
          dispatch(setTriggerPoint(activeNewIdeaTrigger));
          return;
        }

        const inactiveGate = {
          triggeredSource: "new_idea",
          isTriggeredPointActive: false,
          pendingProfileGate: true,
          ...triggerPayloadExtras()
        };
        setTriggerPointCookie(inactiveGate);
        dispatch(setTriggerPoint(inactiveGate));
        dispatch(setModalStatus("create"));
        requireProfileCompletion({
          reason: "profileGate.requiredForContribution",
          source: "ideas"
        });
        return;
      }

      onOpenHandler();
      addQueryParam();
      return;
    }

    // If user is not registered, redirect to login modal
    if (!isUnverfiedUserLoggedIn && !isRegisteredUserLoggedIn) {
      dispatch(setModalStatus("create"));
      if (guestParticipationAllowed) {
        const inactiveNewIdeaTrigger = {
          triggeredSource: "new_idea",
          isTriggeredPointActive: false,
          ...triggerPayloadExtras()
        };
        setTriggerPointCookie(inactiveNewIdeaTrigger);
        dispatch(setTriggerPoint(inactiveNewIdeaTrigger));
        openGuestLoginModal({ action: "new_idea" });
      } else {
        const activeNewIdeaTrigger = {
          triggeredSource: "new_idea",
          isTriggeredPointActive: true,
          ...triggerPayloadExtras()
        };
        setTriggerPointCookie(activeNewIdeaTrigger);
        dispatch(setTriggerPoint(activeNewIdeaTrigger));
      }
      return;
    }

    onOpenHandler();
    addQueryParam();
  };

  useEffect(() => {
    if (
      action === "new_idea" &&
      (triggerPoint.triggeredSource === "new_idea" ||
        cookieTriggerPoint?.triggeredSource === "new_idea")
    )
      onOpenHandler();
  }, [onOpenHandler, action, triggerPoint]);

  const addQueryParam = () => {
    router.query.action = "new_idea";
    router.replace(router, "", { shallow: true });
  };

  const newContributionLocale = toSentenceCase(
    translate("tools.newContribution", {
      contributionName:
        contributionName.trim() ||
        translate(`tools.${getToolLocaleKeyFromLayout(layout)}.callToAction`)
    })
  );

  return (
    <>
      {!isIdeaBoardEmpty && (
        <>
          <IconButton
            aria-label={newContributionLocale}
            icon={<FiEdit />}
            w={{ base: 10, sm: 12, xl: 10 }}
            h={{ base: 10, sm: 12, xl: 10 }}
            variant="primary"
            borderRadius="50%"
            boxShadow="lg"
            onClick={handleClick}
            disabled={isButtonDisabled}
            hidden={!isMobileScreen}
            data-testid="ideas-create-trigger-icon"
            css={{
              "& > svg": {
                height: 5,
                width: 5
              }
            }}
          />
          <Button
            variant="primary"
            size={{ base: "lg", xl: "md" }}
            leftIcon={<FiEdit />}
            fontWeight="semibold"
            fontSize="md"
            py={2}
            px={4}
            onClick={handleClick}
            disabled={isButtonDisabled}
            hidden={isMobileScreen}
            data-testid="ideas-create-trigger-button"
          >
            {newContributionLocale}
          </Button>
        </>
      )}
    </>
  );
};

export default ActionButton;
